// BILLING-PLANS (decided 2026-10-28): what lowers the bill takes effect at the renewal, never
// credited mid-period. The client's Plan card and the operator's company row list those waiting
// changes, each with the day it takes effect; the operator can cancel a plan change (its terms
// sent back, at no charge). A DE's own scheduled downgrade shows on its row (`ScheduledTier`).
import { Button, InfoTip } from "@companyos/ui";
import { formatDate, formatMoney } from "../../format";
import type { MaintenanceTier, SubscriptionView } from "../plan-queries";
import { TIER_LABEL } from "./parts";

type Scheduled = NonNullable<SubscriptionView["scheduled"]>;
type Item = Scheduled["items"][number];

function itemText(i: Item, currency: string): string {
  const money = (v: number | null) => (v === null ? "—" : formatMoney(v, currency));
  const priced =
    i.from_unit_minor !== null && i.to_unit_minor !== null && i.from_unit_minor !== i.to_unit_minor;
  const name =
    i.item === "plan"
      ? "Plan"
      : i.item === "extra_seats"
        ? "Extra people"
        : i.item === "maintenance"
          ? `${i.tier ? TIER_LABEL[i.tier] : ""} maintenance`.trim()
          : "Retainer";
  if (priced) return `${name}: ${money(i.from_unit_minor)} → ${money(i.to_unit_minor)}`;
  if (i.item === "plan") return name;
  return `${name}: ${i.from_quantity} → ${i.to_quantity}`;
}

export function ScheduledChanges({
  scheduled,
  currency,
  onCancel,
  cancelling = false,
}: {
  scheduled: Scheduled | null;
  currency: string;
  /** Operator only, when a plan change caused it: sends the paid terms back. */
  onCancel?: (() => void) | undefined;
  cancelling?: boolean;
}) {
  if (!scheduled || scheduled.items.length === 0) return null;
  const on = scheduled.effective_at ? formatDate(scheduled.effective_at) : "the renewal";
  return (
    <div data-slot="billing-scheduled" className="flex flex-col gap-1 text-[13px]">
      <p className="inline-flex items-center gap-1 font-medium">
        Scheduled changes
        <InfoTip label="About scheduled changes">
          A lower price or fewer items is never credited mid-period. This period is already paid, so
          the change starts with the next invoice.
        </InfoTip>
      </p>
      <ul className="flex flex-col gap-0.5">
        {scheduled.items.map((i) => (
          <li
            key={`${i.item}:${i.tier ?? ""}:${i.from_quantity}:${i.to_quantity}`}
            className="flex flex-wrap items-center gap-x-2"
          >
            <span>{itemText(i, currency)}</span>
            <span className="text-muted-foreground">Takes effect on {on}</span>
          </li>
        ))}
      </ul>
      {onCancel && scheduled.revert ? (
        <div>
          <Button variant="ghost" size="sm" disabled={cancelling} onClick={onCancel}>
            Cancel plan change
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** A DE's downgrade waiting for the renewal: "→ Standard · Takes effect on …". */
export function ScheduledTier({
  tier,
  at,
  onCancel,
  cancelling = false,
}: {
  tier: MaintenanceTier | null;
  at: string | null;
  onCancel?: (() => void) | undefined;
  cancelling?: boolean;
}) {
  if (!tier) return null;
  return (
    <span
      data-field="scheduled-tier"
      className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground"
    >
      <span>
        → {TIER_LABEL[tier]} · Takes effect on {at ? formatDate(at) : "the renewal"}
      </span>
      {onCancel ? (
        <Button
          variant="link"
          size="xs"
          className="h-auto p-0"
          disabled={cancelling}
          onClick={onCancel}
        >
          Cancel
        </Button>
      ) : null}
    </span>
  );
}
